import {Station, StationType} from "../model/station";
import {IGameSprite} from "./game_sprite_base";
import {KitchenScene} from "./scene";
import Graphics = Phaser.GameObjects.Graphics;
import Sprite = Phaser.GameObjects.Sprite;

const TILE_SIZE = 32;
const PROGRESS_BAR_HEIGHT = 6;
const ICON_SCALE = 2; // 16px icons scaled to 32px tiles
// Progress bar is drawn above the tile top, offset by 2px gap
const PROGRESS_BAR_Y = -TILE_SIZE / 2 - PROGRESS_BAR_HEIGHT - 2;

const STATION_ASSET_KEY: Partial<Record<StationType, string>> = {
    [StationType.CUTTING_BOARD]: "station_cutting_board",
    [StationType.STOVE]: "station_stove",
};

export class StationSprite extends Phaser.GameObjects.Container implements IGameSprite<Station> {
    instance: Station
    private icon: Sprite | null
    private trashIcon: Graphics | null
    private progressBar: Graphics
    private statusLabel: Phaser.GameObjects.Text | null = null

    constructor(scene: KitchenScene, instance: Station) {
        super(scene, instance.x, instance.y);
        this.instance = instance;

        const assetKey = STATION_ASSET_KEY[instance.stationType];
        if (assetKey) {
            this.icon = scene.add.sprite(0, 0, assetKey).setScale(ICON_SCALE);
            this.trashIcon = null;
        } else if (instance.stationType === StationType.TRASH) {
            this.icon = null;
            this.trashIcon = this.drawTrashIcon(scene);
            const label = scene.add.text(0, 0, 'BIN', {fontSize: '10px', color: '#fff', fontStyle: 'bold'}).setOrigin(0.5);
            this.add(label);
        } else {
            // COUNTER and other types: tilemap furniture tile already provides the visual
            this.icon = null;
            this.trashIcon = null;
        }
        this.progressBar = scene.add.graphics();
        this.add([this.icon ?? this.trashIcon, this.progressBar].filter(Boolean));

        const HIT_SIZE = Math.max(44, TILE_SIZE);
        this.setSize(HIT_SIZE, HIT_SIZE);
        this.setInteractive(
            new Phaser.Geom.Rectangle(-HIT_SIZE / 2, -HIT_SIZE / 2, HIT_SIZE, HIT_SIZE),
            Phaser.Geom.Rectangle.Contains
        );

        this.bindToInstance();
    }

    private drawTrashIcon(scene: KitchenScene): Graphics {
        const g = scene.add.graphics();
        // Red background square filling the tile
        g.fillStyle(0xcc2222, 0.85);
        g.fillRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE);
        // White border
        g.lineStyle(2, 0xffffff, 1);
        g.strokeRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE);
        // White X across the tile
        g.lineStyle(3, 0xffffff, 1);
        g.strokeLineShape(new Phaser.Geom.Line(-10, -10, 10, 10));
        g.strokeLineShape(new Phaser.Geom.Line(10, -10, -10, 10));
        return g;
    }

    bindToInstance() {
        this.instance.onChange(() => {
            this.updateProgressBar();
        });
    }

    private updateProgressBar() {
        this.progressBar.clear();

        if (this.instance.burnProgress > 0) {
            // Burn warning bar: orange -> red, drawn above tile
            const burnRatio = this.instance.burnProgress;
            const green = Math.floor(0x88 * (1 - burnRatio));
            const color = (0xFF << 16) | (green << 8);
            const barWidth = Math.floor(TILE_SIZE * burnRatio);

            // Background track
            this.progressBar.fillStyle(0x333333, 0.6);
            this.progressBar.fillRect(-TILE_SIZE / 2, PROGRESS_BAR_Y, TILE_SIZE, PROGRESS_BAR_HEIGHT);
            // Burn bar
            this.progressBar.fillStyle(color, 1);
            this.progressBar.fillRect(-TILE_SIZE / 2, PROGRESS_BAR_Y, barWidth, PROGRESS_BAR_HEIGHT);
            // White outline for visibility
            this.progressBar.lineStyle(1, 0xffffff, 0.8);
            this.progressBar.strokeRect(-TILE_SIZE / 2, PROGRESS_BAR_Y, TILE_SIZE, PROGRESS_BAR_HEIGHT);

            this.updateStatusLabel(burnRatio >= 0.7 ? 'BURNING!' : 'HOT', burnRatio >= 0.7 ? '#ff3333' : '#ffaa00');
            return;
        }

        if (this.instance.progress <= 0) {
            this.updateStatusLabel(null, null);
            return;
        }

        const barWidth = Math.floor(TILE_SIZE * this.instance.progress);

        // Background track
        this.progressBar.fillStyle(0x333333, 0.6);
        this.progressBar.fillRect(-TILE_SIZE / 2, PROGRESS_BAR_Y, TILE_SIZE, PROGRESS_BAR_HEIGHT);
        // Progress bar
        this.progressBar.fillStyle(0x00FF00, 1);
        this.progressBar.fillRect(-TILE_SIZE / 2, PROGRESS_BAR_Y, barWidth, PROGRESS_BAR_HEIGHT);
        // White outline for visibility
        this.progressBar.lineStyle(1, 0xffffff, 0.8);
        this.progressBar.strokeRect(-TILE_SIZE / 2, PROGRESS_BAR_Y, TILE_SIZE, PROGRESS_BAR_HEIGHT);

        this.updateStatusLabel(null, null);
    }

    private updateStatusLabel(text: string | null, color: string | null): void {
        if (!text) {
            if (this.statusLabel) {
                this.statusLabel.setVisible(false);
            }
            return;
        }
        if (!this.statusLabel) {
            this.statusLabel = (this.scene as KitchenScene).add.text(0, -TILE_SIZE / 2 - 8, '', {
                fontSize: '8px', fontStyle: 'bold'
            }).setOrigin(0.5, 1);
            this.add(this.statusLabel);
        }
        this.statusLabel.setText(text);
        this.statusLabel.setColor(color);
        this.statusLabel.setVisible(true);
    }
}
